import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import type { AuthError } from '@supabase/supabase-js';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as AuthError).message?.toLowerCase() ?? '';
    if (msg.includes('invalid login credentials') || msg.includes('invalid email or password')) {
      return 'Invalid email or password';
    }
    if (msg.includes('email not confirmed') || msg.includes('inactive') || msg.includes('disabled')) {
      return 'Your account is inactive. Contact your administrator.';
    }
    if (msg.includes('fetch') || msg.includes('network') || msg.includes('failed to fetch')) {
      return 'Connection error. Check your network and try again.';
    }
  }
  return 'Sign in failed. Try again.';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, session, isInitialized } = useAuthStore();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (isInitialized && session) {
      navigate('/home', { replace: true });
    }
  }, [isInitialized, session, navigate]);

  const onSubmit = async (data: LoginForm) => {
    setServerError(null);
    try {
      await signIn(data.email, data.password);
      navigate('/home', { replace: true });
    } catch (err) {
      setServerError(getErrorMessage(err));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-mozaic-navy px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-2 text-center">
          <img
            src="/mozaic-logo.png"
            alt="MOZAIC"
            className="mx-auto h-12 w-12"
          />
          <h1 className="text-2xl font-bold tracking-tight text-mozaic-teal">
            MOZAIC
          </h1>
          <p className="text-sm text-muted-foreground">
            Sign in to your account
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {serverError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <div className="flex items-center justify-between">
                  <span>{serverError}</span>
                  <button
                    type="button"
                    onClick={() => setServerError(null)}
                    className="ml-2 text-destructive hover:opacity-70"
                    aria-label="Dismiss"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>

            <p className="text-right text-xs">
              <Link
                to="/auth/forgot-password"
                className="text-mozaic-blue hover:underline"
              >
                Forgot password?
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
